import type { AccountView, CardView } from "../api/types.js";
import { formatMoney, lastDigits, statementDate } from "../lib/format.js";
import { cardDue, isDue } from "./card-due.js";

/**
 * 15 Cards (DCTA-111 run 2; UX spec §E.4/§E.6, mock-up 15-cards.html) from `GET /v1/cards` and
 * `GET /v1/accounts`. The art's words, the status line and what the fast path can do — nothing the
 * bank did not serve: no holder's name on the art, no card limit or controls (backend asks: card
 * `daily_limit`/`spent_today`, ASK-5), no "Frozen at 9:42" (the bank keeps no freeze time we can read).
 *
 * The lab's fast path freezes the cards linked to an account (`freeze_card` + `target_account_id`);
 * the credit card has no account behind it, so it has no target and no Freeze here (backend ask:
 * a card-level target) — never "freeze every card" for a tap on one.
 */
export interface CardModel {
  readonly id: string;
  /** The screen's title: "Debit card", "Platinum Card". */
  readonly title: string;
  readonly chip: string;
  readonly chipLabel: string;
  readonly k1: string;
  readonly k2: string;
  readonly last4: string;
  readonly expiry: string;
  readonly plat: boolean;
  readonly frozen: boolean;
  readonly artLabel: string;
  /** The status line: one bold word or figure, then the rest. */
  readonly status: { readonly strong: string; readonly rest: string };
  /** The account the fast path aims at; absent = no Freeze offered. */
  readonly freezeTarget?: string;
  readonly kind: "debit" | "credit";
}

function digits(maskedPan: string): string {
  return lastDigits(maskedPan).replace(/^···/, "");
}

function one(c: CardView, accounts: readonly AccountView[]): CardModel {
  const credit = c.type === "credit";
  const last4 = digits(c.masked_pan);
  const name = credit ? (c.display_name ?? "Credit card") : "Debit card";
  const k1 = credit ? (c.display_name ?? "Credit").replace(/\s+Card$/i, "") : "Debit";
  const linked = c.account_id === undefined ? undefined : accounts.find((a) => a.account_id === c.account_id);
  const frozen = c.frozen === true;
  const currency = c.currency ?? "SGD";
  // DCTA-131: the minimum still DUE (the bank's `minimum_due_now`), never the statement's own once it is met.
  const minimum = cardDue(c).minimum;
  const status = frozen
    ? { strong: "Frozen", rest: " · nothing new can be charged to it" }
    : credit && c.current_balance !== undefined
      ? {
          strong: formatMoney(c.current_balance, currency),
          rest: ` owed${isDue(minimum) ? ` · minimum ${formatMoney(minimum as string, currency)}${c.due_date === undefined ? "" : ` by ${statementDate(`${c.due_date}T04:00:00Z`)}`}` : ""}`,
        }
      : { strong: "Active", rest: linked === undefined ? "" : ` · linked to ${linked.display_name} ${lastDigits(linked.masked_number)}` };
  return {
    id: c.card_id,
    title: name,
    chip: k1,
    chipLabel: `${name} ending ${last4}`,
    k1,
    k2: credit ? "Credit" : (linked?.display_name ?? ""),
    last4,
    expiry: c.expiry_masked === undefined ? "" : `Exp ${c.expiry_masked}`,
    plat: credit,
    frozen,
    artLabel: `${name} ending ${last4}, ${frozen ? "frozen" : "active"}`,
    status,
    ...(c.account_id === undefined ? {} : { freezeTarget: c.account_id }),
    kind: credit ? "credit" : "debit",
  };
}

export function cardsModel(cards: readonly CardView[], accounts: readonly AccountView[]): readonly CardModel[] {
  const order = (c: CardView): number => (c.type === "credit" ? 1 : 0);
  return [...cards].sort((a, b) => order(a) - order(b)).map((c) => one(c, accounts));
}

/**
 * Which card a screen opens on: the one asked for by id, or by the account behind it (an account's own
 * "Card details"); else — Security's "Freeze a card" — the first card the fast path can freeze, never a
 * card with no account behind it; else the first card.
 */
export function pickCard(cards: readonly CardModel[], wanted: string | undefined): string | undefined {
  if (wanted !== undefined) {
    const hit = cards.find((c) => c.id === wanted) ?? cards.find((c) => c.freezeTarget === wanted);
    if (hit !== undefined) return hit.id;
  }
  return (cards.find((c) => !c.frozen && c.freezeTarget !== undefined) ?? cards[0])?.id;
}
